import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useHistory } from "react-router-dom";
import {
  FiZap,
  FiPlus,
  FiX,
  FiType,
  FiImage,
  FiLink,
  FiMusic,
  FiVideo,
  FiArrowUp,
  FiArrowDown,
  FiArrowLeft,
  FiEdit2,
  FiTrash2,
  FiMessageSquare,
  FiSend,
  FiLock,
  FiAlertTriangle,
  FiChevronDown,
  FiPower,
  FiSmile,
  FiMeh,
  FiFrown,
  FiHash,
  FiActivity,
  FiInfo,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import DashboardLayout from "../../UI/Layout/DashboardLayout";
import styles from "./Instagram.module.css";
import {
  fetchAutomationRules,
  setupAutomation,
  updateAutomationRule,
  deleteAutomationRule,
  fetchProfileInfo,
  fetchInstagramAccountData,
} from "./instagramApi";

const instagramPng = "/img/instagram.png";

// Reply block types a rule can chain together, in order.
const BLOCK_TYPES = [
  {
    value: "text",
    label: "Text",
    icon: FiType,
    placeholder: "Type the reply message...",
  },
  {
    value: "image",
    label: "Image",
    icon: FiImage,
    placeholder: "Enter the URL",
  },
  { value: "url", label: "Link", icon: FiLink, placeholder: "Enter the URL" },
  {
    value: "audio",
    label: "Audio",
    icon: FiMusic,
    placeholder: "Enter the URL",
  },
  {
    value: "video",
    label: "Video",
    icon: FiVideo,
    placeholder: "Enter the URL",
  },
];

const COMMENT_DISALLOWED_TYPES = ["audio", "video"];

const availableBlockTypes = (location) =>
  location === "comment"
    ? BLOCK_TYPES.filter(
      (block) => !COMMENT_DISALLOWED_TYPES.includes(block.value),
    )
    : BLOCK_TYPES;

const MATCH_LOCATIONS = [
  {
    value: "comment",
    label: "Comments",
    description: "Publicly reply under the comment",
    icon: FiMessageSquare,
  },
  {
    value: "conversation",
    label: "Direct Message",
    description: "Reply in their inbox",
    icon: FiSend,
  },
];

const TRIGGER_TYPES = [
  { value: "keyword", label: "Keywords", icon: FiHash },
  { value: "sentiment", label: "Sentiment", icon: FiActivity },
];

const SENTIMENT_OPTIONS = [
  {
    value: "positive",
    label: "Positive",
    icon: FiSmile,
    chipClass: "sentimentChipPositive",
  },
  {
    value: "neutral",
    label: "Neutral",
    icon: FiMeh,
    chipClass: "sentimentChipNeutral",
  },
  {
    value: "negative",
    label: "Negative",
    icon: FiFrown,
    chipClass: "sentimentChipNegative",
  },
];

//adding pagination for the saved rule panel


// --- Keyword business validation ---
const KEYWORD_REGEX = /^[a-zA-Z0-9\s'&_-]{1,25}$/;
const MAX_KEYWORDS = 30;
const MAX_KEYWORD_LENGTH = 20;

const blockMeta = (type) =>
  BLOCK_TYPES.find((block) => block.value === type) || BLOCK_TYPES[0];

const makeId = () => Math.random().toString(36).slice(2, 10);

// Builds a compact page-number list for the Rules panel pagination, e.g.
// [1, "...", 4, 5, 6, "...", 12] — mirrors the ellipsis pattern already
// styled by .paginationEllipsis for the report page's table pagination.
const getRulesPageNumbers = (current, total) => {
  if (total <= 5) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages = new Set([1, total, current, current - 1, current + 1]);
  const sorted = Array.from(pages)
    .filter((p) => p >= 1 && p <= total)
    .sort((a, b) => a - b);

  const withEllipsis = [];
  sorted.forEach((page, idx) => {
    if (idx > 0 && page - sorted[idx - 1] > 1) {
      withEllipsis.push("...");
    }
    withEllipsis.push(page);
  });
  return withEllipsis;
};

const emptyBlock = (type = "text") => ({
  id: makeId(),
  type,
  content: "", // value sent to the backend (text, or link/image/audio/video URL)
  caption: "",
});

const InstagramAutomationSetup = () => {
  const loginData = JSON.parse(localStorage.getItem("login"));
  const userId = loginData?.user?.userId;
  // token lives at the top level of the stored login object, as a sibling
  // of "user" — not nested inside it: { success, user, token, url }
  const token = loginData?.token;
  // Multi-account — reactive so switching in the header refetches rules/media.
  const [activeAccountId, setActiveAccountId] = useState(
    () => localStorage.getItem("instagramActiveAccountId") || "all",
  );
  const scopedAccountId =
    activeAccountId && activeAccountId !== "all" ? activeAccountId : undefined;

  const [connectedAccounts, setConnectedAccounts] = useState(() => {
    try {
      const saved = localStorage.getItem("instagramAccounts");
      const parsed = saved ? JSON.parse(saved) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  const handleSelectAccount = (accountId) => {
    setActiveAccountId(accountId);
    localStorage.setItem("instagramActiveAccountId", accountId);
    window.dispatchEvent(
      new CustomEvent("instagram-account-changed", {
        detail: { accountId },
      }),
    );
  };

  // Keep in sync when another Instagram page changes the active account
  // or the connected-account list itself (e.g. a disconnect elsewhere).
  useEffect(() => {
    const sync = (accountId) => {
      if (!accountId) return;
      setActiveAccountId(accountId);
    };
    const syncAccounts = (accounts) => {
      if (!Array.isArray(accounts)) return;
      setConnectedAccounts(accounts);
    };
    const onCustom = (e) => sync(e.detail?.accountId);
    const onAccountsCustom = (e) => syncAccounts(e.detail?.accounts);
    const onStorage = (e) => {
      if (e.key === "instagramActiveAccountId" && e.newValue) sync(e.newValue);
      if (e.key === "instagramAccounts") {
        try {
          syncAccounts(e.newValue ? JSON.parse(e.newValue) : []);
        } catch {
          // Ignore malformed storage payloads.
        }
      }
    };
    window.addEventListener("instagram-account-changed", onCustom);
    window.addEventListener("instagram-accounts-changed", onAccountsCustom);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("instagram-account-changed", onCustom);
      window.removeEventListener(
        "instagram-accounts-changed",
        onAccountsCustom,
      );
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  // This page only ever shows one connected account at a time (no "All
  // Accounts" combined view here — that's Report-page-only). If the
  // active selection is still "all" — the default, or left over from
  // switching accounts on the Report page — or points at an account that
  // no longer exists (it was just disconnected, possibly from another
  // tab) — fall back to the first connected account as soon as the list
  // is known.
  useEffect(() => {
    if (connectedAccounts.length === 0) return;

    const stillExists =
      activeAccountId !== "all" &&
      connectedAccounts.some(
        (account) =>
          String(account.id) === String(activeAccountId) ||
          String(account.instaUserId) === String(activeAccountId),
      );

    if (!stillExists) {
      handleSelectAccount(connectedAccounts[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAccountId, connectedAccounts]);

  // Fixed header measurement (same approach as Instagram Integration page).
  const stickyHeaderRef = useRef(null);
  const [stickyHeaderOffset, setStickyHeaderOffset] = useState(64);
  const [fixedBarHeight, setFixedBarHeight] = useState(64);
  const [fixedBarLeft, setFixedBarLeft] = useState(0);
  const [fixedBarWidth, setFixedBarWidth] = useState("100%");

  useEffect(() => {
    const measure = () => {
      const globalNavbar = document.querySelector(".navbar.dashboard-header");
      if (globalNavbar) {
        const top = Math.max(0, Math.ceil(globalNavbar.getBoundingClientRect().bottom));
        if (top > 0) setStickyHeaderOffset(top);
      }
      const mainEl = document.querySelector(".main");
      if (mainEl) {
        const r = mainEl.getBoundingClientRect();
        setFixedBarLeft(Math.max(0, Math.round(r.left)));
        setFixedBarWidth(`${Math.round(r.width)}px`);
      }
      if (stickyHeaderRef.current) {
        const h = Math.ceil(stickyHeaderRef.current.getBoundingClientRect().height);
        if (h > 0) setFixedBarHeight(h);
      }
    };
    measure();
    const raf = requestAnimationFrame(measure);
    const t = setTimeout(measure, 120);
    const globalNavbar = document.querySelector(".navbar.dashboard-header");
    const mainEl = document.querySelector(".main");
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (ro && globalNavbar) ro.observe(globalNavbar);
    if (ro && mainEl) ro.observe(mainEl);
    if (ro && stickyHeaderRef.current) ro.observe(stickyHeaderRef.current);
    window.addEventListener("resize", measure);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
      if (ro) ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [connectedAccounts.length]);

  const handleConnectInstagram = () => {
    const INSTAGRAM_APP_ID = process.env.REACT_APP_INSTAGRAM_APP_ID;
    const INSTAGRAM_PERMISSION = process.env.REACT_APP_INSTAGRAM_PERMISSION;
    const INSTAGRAM_AUTH_ENDPOINT = process.env.REACT_APP_INSTAGRAM_AUTH_ENDPOINT;
    const INSTAGRAM_REDIRECT_URI = process.env.REACT_APP_INSTAGRAM_REDIRECT_URI;
    if (!userId || !INSTAGRAM_APP_ID) return;
    try {
      const authUrl = `${INSTAGRAM_AUTH_ENDPOINT}?force_reauth=true&client_id=${encodeURIComponent(
        INSTAGRAM_APP_ID,
      )}&redirect_uri=${encodeURIComponent(
        INSTAGRAM_REDIRECT_URI,
      )}&response_type=code&scope=${encodeURIComponent(
        INSTAGRAM_PERMISSION,
      )}&state=${encodeURIComponent(userId)}`;
      window.location.assign(authUrl);
    } catch {
      // ignore
    }
  };

  const location = useLocation();
  const history = useHistory();

  // ---- optional post scope (arrives via the "Automate Reply" button on a
  // post card, or a ?mediaId= link). When present, rules created here are
  // tied to that single Instagram post instead of the whole account. ----
  const initialScopedMedia = useMemo(() => {
    const stateMediaId = location.state?.mediaId;
    if (stateMediaId) {
      return {
        mediaId: String(stateMediaId),
        thumbnail: location.state?.mediaThumbnail || null,
        caption: location.state?.mediaCaption || "",
      };
    }

    const params = new URLSearchParams(location.search);
    const queryMediaId = params.get("mediaId");
    if (queryMediaId) {
      return { mediaId: queryMediaId, thumbnail: null, caption: "" };
    }

    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [scopedMedia, setScopedMedia] = useState(initialScopedMedia);
  const scopedMediaId = scopedMedia?.mediaId || null;
  const [postSelectorOpen, setPostSelectorOpen] = useState(false);

  const handleClearMediaScope = () => {
    setScopedMedia(null);
    resetForm();
    history.replace("/dashboard/instagram-automation");
  };

  const handleSelectPost = (mediaId) => {
    if (mediaId === scopedMediaId) {
      setPostSelectorOpen(false);
      return;
    }
    const postInfo = getPostInfo(mediaId);
    setScopedMedia({
      mediaId,
      thumbnail: postInfo?.thumbnail || null,
      caption: postInfo?.caption || "",
    });
    resetForm();
    setPostSelectorOpen(false);
    history.replace(`/dashboard/instagram-automation?mediaId=${mediaId}`);
  };

  const [isConnected, setIsConnected] = useState(
    () => localStorage.getItem("instagram_connected") === "true",
  );
  const [connectionChecked, setConnectionChecked] = useState(false);

  // ---- lookup of every post's thumbnail/caption by media id, so rule
  // cards and the "editing" banner can show a picture even when the rule
  // wasn't opened via the "Automate Reply" button on that post. ----
  const [mediaMap, setMediaMap] = useState({});

  const loadMediaMap = async () => {
    if (!userId) return;

    try {
      const result = await fetchInstagramAccountData({
        userId,
        instaUserId: scopedAccountId,
      });
      const payload = result?.data || result;
      const items = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.data?.data)
            ? payload.data.data
            : [];

      const map = {};
      items.forEach((item) => {
        if (!item?.id) return;
        map[String(item.id)] = {
          mediaId: String(item.id),
          thumbnail:
            item.media_type === "VIDEO"
              ? item.thumbnail_url || null
              : item.media_url || null,
          caption: item.caption || "",
        };
      });

      setMediaMap(map);
    } catch (error) {
      // Non-fatal — rule cards / banners just fall back to showing the raw
      // post ID without a thumbnail if this fails.
    }
  };

  // Merge the router-provided scoped post (has the freshest thumbnail/
  // caption) with whatever we fetched into mediaMap, keyed by post id.
  const getPostInfo = (mediaId) => {
    if (!mediaId) return null;
    const id = String(mediaId);

    if (scopedMediaId === id && scopedMedia) {
      return {
        mediaId: id,
        thumbnail: scopedMedia.thumbnail || mediaMap[id]?.thumbnail || null,
        caption: scopedMedia.caption || mediaMap[id]?.caption || "",
      };
    }

    return mediaMap[id] || { mediaId: id, thumbnail: null, caption: "" };
  };

  // ---- rules list state ----
  const [rules, setRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // ---- rules panel pagination (display-only; never touches `rules` data,
  // API calls, or any of the handlers above — those all still read/write
  // the full `rules` array) ----
  const RULES_PAGE_SIZE = 3;
  const [rulesPage, setRulesPage] = useState(1);
  const totalRulesPages = Math.max(
    1,
    Math.ceil(rules.length / RULES_PAGE_SIZE),
  );
  // Keep the current page in range if the list shrinks (e.g. a rule on the
  // last page gets deleted) or grows from elsewhere.
  useEffect(() => {
    setRulesPage((prev) => Math.min(Math.max(prev, 1), totalRulesPages));
  }, [totalRulesPages]);
  const paginatedRules = useMemo(() => {
    const start = (rulesPage - 1) * RULES_PAGE_SIZE;
    return rules.slice(start, start + RULES_PAGE_SIZE);
  }, [rules, rulesPage]);

  // ---- saved-rule reply preview state ----
  // Purely a display concern: each saved rule's reply text is clamped to
  // 3 lines in the card, and "Read more" opens a modal with the full,
  // unclamped content for that rule. Doesn't touch rule data or any API.
  const [readMoreRule, setReadMoreRule] = useState(null);
  const [clampedBlocks, setClampedBlocks] = useState({});
  const measureReplyBlock = (el, blockKey) => {
    if (!el) return;
    const isClamped = el.scrollHeight > el.clientHeight + 1;
    setClampedBlocks((prev) =>
      prev[blockKey] === isClamped ? prev : { ...prev, [blockKey]: isClamped },
    );
  };

  // ---- DM 24-hour messaging window info modal ----
  // Purely informational — explains Instagram's messaging window policy
  // when the user taps the info icon next to the "Direct Message" reply
  // destination option. Doesn't touch any rule data, save/toggle/delete
  // behaviour, or the matchLocation selection itself.
  const [showDmWindowInfo, setShowDmWindowInfo] = useState(false);

  // ---- rule tester state (client-side simulation only — doesn't call any
  // API, so it can't affect existing save/toggle/delete functionality).
  // Lives in a modal opened from the page header. ----
  const [testerModalOpen, setTesterModalOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const chatWindowRef = useRef(null);

  // ---- form state ----
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [editingRuleMediaId, setEditingRuleMediaId] = useState(null);
  const [triggerType, setTriggerType] = useState("keyword"); // "keyword" | "sentiment"
  const [keywords, setKeywords] = useState([]);
  const [keywordDraft, setKeywordDraft] = useState("");
  const [sentiments, setSentiments] = useState([]); // "positive" | "neutral" | "negative"
  const [matchLocation, setMatchLocation] = useState("comment");
  const [replyBlocksByLocation, setReplyBlocksByLocation] = useState({
    comment: [emptyBlock("text")],
    conversation: [emptyBlock("text")],
  });
  const replyBlocks = replyBlocksByLocation[matchLocation];
  const usedBlockTypes = replyBlocks.map((block) => block.type);
  const [saving, setSaving] = useState(false);

  const [statusMessage, setStatusMessage] = useState("");
  const [statusType, setStatusType] = useState("success"); // "success" | "error"

  // Both success and error banners are transient — clear them a few
  // seconds after they appear instead of leaving them on screen.
  useEffect(() => {
    if (!statusMessage) return;

    const timer = setTimeout(
      () => setStatusMessage(""),
      statusType === "error" ? 4500 : 3500,
    );

    return () => clearTimeout(timer);
  }, [statusMessage, statusType]);

  // Tracks the most recently *started* loadRules() call. If the post scope
  // changes (e.g. the user clicks "View all automation rules") while an
  // earlier, still-scoped request is in flight, that earlier request's
  // response can arrive after the new one and silently overwrite it with a
  // stale, filtered list. Comparing against this ref lets us ignore any
  // response that isn't from the latest call.
  const rulesRequestIdRef = useRef(0);

  const loadRules = async () => {
    if (!userId || !token) return;

    const requestId = ++rulesRequestIdRef.current;
    const requestedMediaId = scopedMediaId;

    try {
      setRulesLoading(true);
      const response = await fetchAutomationRules({
        userId,
        token,
        mediaId: requestedMediaId || undefined,
        instaUserId: scopedAccountId,
      });

      // A newer loadRules() call has since started — this response is
      // stale, so drop it instead of letting it clobber fresher data.
      if (requestId !== rulesRequestIdRef.current) return;

      const list = response?.data || response?.rules || [];
      const allRules = Array.isArray(list) ? list : [];

      // Defensive client-side filter in case the backend returns every
      // rule regardless of the media_id we asked for.
      const visibleRules = requestedMediaId
        ? allRules.filter((rule) => String(rule.mediaId) === requestedMediaId)
        : allRules;

      setRules(visibleRules);
    } catch (error) {
      if (requestId !== rulesRequestIdRef.current) return;
      setStatusType("error");
      setStatusMessage(error.message || "Unable to load automation rules.");
    } finally {
      if (requestId === rulesRequestIdRef.current) {
        setRulesLoading(false);
      }
    }
  };

  // Verify the connection status against the backend once on mount, no
  // matter what localStorage said.
  useEffect(() => {
    let cancelled = false;

    const verifyConnection = async () => {
      if (!userId) {
        setConnectionChecked(true);
        return;
      }

      try {
        const result = await fetchProfileInfo({
          userId,
          instaUserId: scopedAccountId,
        });
        const profilePayload = result?.data || result;
        const profile = Array.isArray(profilePayload)
          ? profilePayload[0]
          : profilePayload?.data || profilePayload;
        const connected = Boolean(
          profile?.username || (result?.success && profile),
        );

        if (cancelled) return;

        setIsConnected(connected);
        if (connected) {
          localStorage.setItem("instagram_connected", "true");
        } else {
          localStorage.removeItem("instagram_connected");
        }
      } catch (error) {
        if (
          !cancelled &&
          localStorage.getItem("instagram_connected") !== "true"
        ) {
          setIsConnected(false);
        }
      } finally {
        if (!cancelled) setConnectionChecked(true);
      }
    };

    verifyConnection();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load rules once connected; re-run when post scope OR active account changes.
  useEffect(() => {
    if (isConnected) {
      loadRules();
      setRulesPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, scopedMediaId, activeAccountId]);

  // Load the post thumbnail/caption lookup once, so rule cards and the
  // "editing" banner can show a picture for any post, not just the one the
  // page happens to be scoped to.
  useEffect(() => {
    if (isConnected) {
      loadMediaMap();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, activeAccountId]);

  const resetForm = () => {
    setEditingRuleId(null);
    setEditingRuleMediaId(null);
    setTriggerType("keyword");
    setKeywords([]);
    setKeywordDraft("");
    setSentiments([]);
    setMatchLocation("comment");
    setReplyBlocksByLocation({
      comment: [emptyBlock("text")],
      conversation: [emptyBlock("text")],
    });
  };

  // If the user switches accounts from the header while a rule list is
  // scoped to a specific post, that post belonged to the *previous*
  // account and no longer applies — drop back to the unscoped "all
  // rules" view instead of silently querying the new account for a post
  // it doesn't own. Skips the very first render so a page opened
  // directly on a post's automation setup (via navigation state) isn't
  // immediately cleared.
  const isFirstAccountRenderRef = useRef(true);
  useEffect(() => {
    if (isFirstAccountRenderRef.current) {
      isFirstAccountRenderRef.current = false;
      return;
    }
    if (scopedMedia) {
      setScopedMedia(null);
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAccountId]);

  const toggleSentiment = (value) => {
    setSentiments((prev) =>
      prev.includes(value)
        ? prev.filter((s) => s !== value)
        : [...prev, value],
    );
  };

  const handleMatchLocationChange = (value) => {
    if (value === "comment") {
      const hasDisallowed = replyBlocksByLocation.comment.some((block) =>
        COMMENT_DISALLOWED_TYPES.includes(block.type),
      );

      if (hasDisallowed) {
        setReplyBlocksByLocation((prev) => {
          const filtered = prev.comment.filter(
            (block) => !COMMENT_DISALLOWED_TYPES.includes(block.type),
          );
          return {
            ...prev,
            comment: filtered.length ? filtered : [emptyBlock("text")],
          };
        });
        setStatusType("error");
        setStatusMessage(
          "Audio and video replies only work for Direct Messages, so they were removed from this rule.",
        );
      }
    }

    setMatchLocation(value);
  };

  // ---- keyword chip handling ----
  const commitKeywordDraft = () => {
    const value = keywordDraft.trim();
    if (!value) return;

    if (keywords.length >= MAX_KEYWORDS) {
      setStatusType("error");
      setStatusMessage(`You can add up to ${MAX_KEYWORDS} keywords per rule.`);
      return;
    }

    if (value.length > MAX_KEYWORD_LENGTH) {
      setStatusType("error");
      setStatusMessage(
        `Keywords can be at most ${MAX_KEYWORD_LENGTH} characters.`,
      );
      return;
    }

    if (!KEYWORD_REGEX.test(value)) {
      setStatusType("error");
      setStatusMessage(
        "Keywords can only contain letters, numbers, spaces, hyphens, underscores, apostrophes, and &.",
      );
      return;
    }

    if (keywords.some((k) => k.toLowerCase() === value.toLowerCase())) {
      setKeywordDraft("");
      return;
    }

    setKeywords((prev) => [...prev, value]);
    setKeywordDraft("");
  };

  const handleKeywordKeyDown = (event) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      commitKeywordDraft();
    } else if (event.key === "Backspace" && !keywordDraft && keywords.length) {
      setKeywords((prev) => prev.slice(0, -1));
    }
  };

  const removeKeyword = (value) => {
    setKeywords((prev) => prev.filter((k) => k !== value));
  };

  // ---- reply block handling (scoped to the currently selected location) ----
  const addBlock = (type) => {
    setReplyBlocksByLocation((prev) => {
      const list = prev[matchLocation];
      if (list.some((block) => block.type === type)) return prev; // one per type
      return { ...prev, [matchLocation]: [...list, emptyBlock(type)] };
    });
  };

  const removeBlock = (id) => {
    setReplyBlocksByLocation((prev) => {
      const list = prev[matchLocation];
      if (list.length <= 1) return prev;
      return {
        ...prev,
        [matchLocation]: list.filter((block) => block.id !== id),
      };
    });
  };

  const updateBlock = (id, patch) => {
    setReplyBlocksByLocation((prev) => ({
      ...prev,
      [matchLocation]: prev[matchLocation].map((block) =>
        block.id === id ? { ...block, ...patch } : block,
      ),
    }));
  };

  // Switching a block's type clears out whatever content belonged to the
  // previous type.
  const changeBlockType = (id, newType) => {
    setReplyBlocksByLocation((prev) => {
      const list = prev[matchLocation];
      if (list.some((block) => block.id !== id && block.type === newType))
        return prev; // one per type
      return {
        ...prev,
        [matchLocation]: list.map((block) =>
          block.id === id
            ? { ...block, type: newType, content: "", caption: "" }
            : block,
        ),
      };
    });
  };

  const moveBlock = (id, direction) => {
    setReplyBlocksByLocation((prev) => {
      const list = prev[matchLocation];
      const index = list.findIndex((block) => block.id === id);
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= list.length) return prev;

      const next = [...list];
      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
      return { ...prev, [matchLocation]: next };
    });
  };

  // ---- edit / save ----
  const handleEditRule = (rule) => {
    setEditingRuleId(rule.id);
    setEditingRuleMediaId(rule.mediaId ? String(rule.mediaId) : null);

    const nextTrigger =
      rule.triggerType === "sentiment" ? "sentiment" : "keyword";
    setTriggerType(nextTrigger);
    setKeywords(nextTrigger === "keyword" ? rule.keywords || [] : []);
    setKeywordDraft("");
    setSentiments(nextTrigger === "sentiment" ? rule.sentiments || [] : []);

    const location =
      rule.matchLocation === "conversation" ? "conversation" : "comment";
    setMatchLocation(location);

    let blocks = rule.replyBlocks?.length
      ? rule.replyBlocks.map((block) => ({
        ...block,
        id: block.id || makeId(),
      }))
      : [emptyBlock("text")];

    if (location === "comment") {
      blocks = blocks.filter(
        (block) => !COMMENT_DISALLOWED_TYPES.includes(block.type),
      );
      if (!blocks.length) blocks = [emptyBlock("text")];
    }

    setReplyBlocksByLocation({
      comment: location === "comment" ? blocks : [emptyBlock("text")],
      conversation: location === "conversation" ? blocks : [emptyBlock("text")],
    });
    setStatusMessage("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const validateForm = () => {
    if (triggerType === "sentiment") {
      if (sentiments.length === 0) {
        return "Select at least one sentiment (Positive, Neutral, or Negative).";
      }
    } else if (keywords.length === 0) {
      return "Add at least one trigger keyword.";
    }

    const hasEmptyBlock = replyBlocks.some(
      (block) => !block.content || !block.content.trim(),
    );
    if (hasEmptyBlock) {
      return "Fill in every reply block, or remove the empty ones.";
    }

    return null;
  };

  const handleSaveRule = async () => {
    setStatusMessage("");

    if (!userId || !token) {
      setStatusType("error");
      setStatusMessage("Unable to find the logged-in user ID or token.");
      return;
    }

    const validationError = validateForm();
    if (validationError) {
      setStatusType("error");
      setStatusMessage(validationError);
      return;
    }

    const targetMediaId = editingRuleId ? editingRuleMediaId : scopedMediaId;

    if (!targetMediaId) {
      setStatusType("error");
      setStatusMessage(
        "Select a post to create this automation rule for — every rule is tied to a specific post.",
      );
      return;
    }

    try {
      setSaving(true);

      const payload = {
        userId,
        token,
        triggerType,
        keywords: triggerType === "keyword" ? keywords : [],
        sentiments: triggerType === "sentiment" ? sentiments : [],
        matchLocation,
        replyBlocks,
        mediaId: targetMediaId,
        instaUserId: scopedAccountId,
      };

      const response = editingRuleId
        ? await updateAutomationRule({ ...payload, ruleId: editingRuleId })
        : await setupAutomation(payload);

      if (response.success === false) {
        setStatusType("error");
        setStatusMessage(
          response.message || "Unable to save the automation rule.",
        );
        return;
      }

      setStatusType("success");
      setStatusMessage(
        editingRuleId ? "Automation rule updated." : "Automation rule created.",
      );
      resetForm();
      loadRules();
    } catch (error) {
      setStatusType("error");
      setStatusMessage(error.message || "Something went wrong.");
    } finally {
      setSaving(false);
    }
  };

  // ---- toggle / delete ----
  const handleToggleRule = async (rule) => {
    if (!userId || !token) return;

    const nextActive = !rule.isActive;
    setTogglingId(rule.id);
    setRules((prev) =>
      prev.map((r) => (r.id === rule.id ? { ...r, isActive: nextActive } : r)),
    );

    try {
      await updateAutomationRule({
        userId,
        token,
        ruleId: rule.id,
        triggerType: rule.triggerType || "keyword",
        keywords: rule.keywords || [],
        sentiments: rule.sentiments || [],
        matchLocation: rule.matchLocation,
        replyBlocks: rule.replyBlocks,
        isActive: nextActive,
        mediaId: rule.mediaId || undefined,
        instaUserId: scopedAccountId,
      });
    } catch (error) {
      // revert on failure
      setRules((prev) =>
        prev.map((r) =>
          r.id === rule.id ? { ...r, isActive: rule.isActive } : r,
        ),
      );
      setStatusType("error");
      setStatusMessage(error.message || "Unable to update the rule status.");
    } finally {
      setTogglingId(null);
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!userId || !token) return;

    try {
      setDeletingId(ruleId);
      await deleteAutomationRule({
        userId,
        token,
        ruleId,
        instaUserId: scopedAccountId,
      });
      setRules((prev) => prev.filter((r) => r.id !== ruleId));
      setConfirmDeleteId(null);
      if (editingRuleId === ruleId) {
        resetForm();
      }
      setReadMoreRule((prev) => (prev && prev.id === ruleId ? null : prev));
    } catch (error) {
      setStatusType("error");
      setStatusMessage(error.message || "Unable to delete the rule.");
    } finally {
      setDeletingId(null);
    }
  };

  // ---- rule tester: simulate a comment/DM against all saved rules'
  // keywords entirely on the client (no API call), so it can't touch
  // save/toggle/delete behaviour or any existing UI. Auto-detects which
  // rule (if any) the typed text would trigger, the same way the real
  // automation would match a live comment. ----
  const handleSendTestMessage = () => {
    const text = chatInput.trim();
    if (!text) return;

    const userMsg = { id: makeId(), role: "user", text };
    const normalizedText = text.toLowerCase();

    // Client-side sandbox: keyword rules match on substring; sentiment
    // rules match when the typed text contains the sentiment label
    // (e.g. "positive") so testers can exercise those rules without NLP.
    const matchedRule = rules.find((rule) => {
      if (rule.triggerType === "sentiment") {
        return (rule.sentiments || []).some((sentiment) =>
          normalizedText.includes(String(sentiment).toLowerCase()),
        );
      }
      return (rule.keywords || []).some((keyword) =>
        normalizedText.includes(String(keyword).toLowerCase()),
      );
    });

    let matchedLabel = null;
    if (matchedRule) {
      if (matchedRule.triggerType === "sentiment") {
        matchedLabel = (matchedRule.sentiments || []).find((sentiment) =>
          normalizedText.includes(String(sentiment).toLowerCase()),
        );
      } else {
        matchedLabel = (matchedRule.keywords || []).find((keyword) =>
          normalizedText.includes(String(keyword).toLowerCase()),
        );
      }
    }

    let resultMsg;
    if (!matchedRule) {
      resultMsg = {
        id: makeId(),
        role: "system",
        kind: "no-match",
        text: "No saved rule matches this text yet. Try a keyword or sentiment label from an active rule.",
      };
    } else if (!matchedRule.isActive) {
      const kindLabel =
        matchedRule.triggerType === "sentiment" ? "sentiment" : "keyword";
      resultMsg = {
        id: makeId(),
        role: "system",
        kind: "inactive",
        text: `This matches the "${matchedLabel}" ${kindLabel}, but that rule is currently turned off — no reply would be sent.`,
      };
    } else {
      resultMsg = {
        id: makeId(),
        role: "system",
        kind: "matched",
        matchedKeyword: matchedLabel,
        triggerType: matchedRule.triggerType || "keyword",
        location: matchedRule.matchLocation,
        blocks: matchedRule.replyBlocks || [],
      };
    }

    setChatMessages((prev) => [...prev, userMsg, resultMsg]);
    setChatInput("");
  };

  const handleClearTesterChat = () => setChatMessages([]);

  // Keep the chat window scrolled to the latest message as the
  // conversation grows.
  useEffect(() => {
    if (chatWindowRef.current) {
      chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
    }
  }, [chatMessages]);

  // ---- still verifying connection: avoid flashing the wrong screen ----
  if (!connectionChecked) {
    return (
      <DashboardLayout>
        <main className="content">
          <div className={styles.instagramPage}>
            <div
              ref={stickyHeaderRef}
              className={styles.fixedPageHeader}
              style={{
                top: stickyHeaderOffset,
                left: fixedBarLeft,
                width: fixedBarWidth,
              }}
            >
              <div className={styles.fixedPageHeaderInner}>
                <div className={styles.pageHeaderLeft}>
                  <Link
                    to="/dashboard/instagram"
                    className={styles.backButtonCompact}
                    aria-label="Back to Instagram"
                  >
                    <FiArrowLeft />
                  </Link>
                  <div className={styles.pageHeaderIcon}>
                    <img src={instagramPng} alt="Instagram" />
                  </div>
                  <div className={styles.pageHeaderTitleRow}>
                    <h1>Instagram Automation Setup</h1>
                  </div>
                </div>
                {isConnected && (
                  <div className={styles.pageHeaderRight}>
                    <div className={styles.accountPillScroller}>
                      {connectedAccounts.map((account) => (
                        <button
                          type="button"
                          key={account.id}
                          className={`${styles.accountPill} ${activeAccountId === account.id
                            ? styles.accountPillActive
                            : ""
                            }`}
                          onClick={() => handleSelectAccount(account.id)}
                        >
                          {account.avatarUrl ? (
                            <img
                              src={account.avatarUrl}
                              alt=""
                              className={styles.accountPillAvatar}
                            />
                          ) : (
                            <span className={styles.accountPillAvatarFallback}>
                              {account.username?.[0]?.toUpperCase() || "?"}
                            </span>
                          )}
                          @{account.username}
                          <span className={styles.accountPillStatusDot} />
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      className={styles.accountSwitcherConnectButton}
                      onClick={handleConnectInstagram}
                    >
                      <FiPlus />
                      Connect IG Account
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div
              className={styles.fixedPageHeaderSpacer}
              style={{ height: fixedBarHeight }}
              aria-hidden="true"
            />

            <p className={styles.ruleEmptyState}>
              Checking your Instagram connection...
            </p>
          </div>
        </main>
      </DashboardLayout>
    );
  }

  // ---- not connected: gate the whole page ----
  if (!isConnected) {
    return (
      <DashboardLayout>
        <main className="content">
          <div className={styles.instagramPage}>
            <div
              ref={stickyHeaderRef}
              className={styles.fixedPageHeader}
              style={{
                top: stickyHeaderOffset,
                left: fixedBarLeft,
                width: fixedBarWidth,
              }}
            >
              <div className={styles.fixedPageHeaderInner}>
                <div className={styles.pageHeaderLeft}>
                  <Link
                    to="/dashboard/instagram"
                    className={styles.backButtonCompact}
                    aria-label="Back to Instagram"
                  >
                    <FiArrowLeft />
                  </Link>
                  <div className={styles.pageHeaderIcon}>
                    <img src={instagramPng} alt="Instagram" />
                  </div>
                  <div className={styles.pageHeaderTitleRow}>
                    <h1>Instagram Automation Setup</h1>
                  </div>
                </div>
                {isConnected && (
                  <div className={styles.pageHeaderRight}>
                    <div className={styles.accountPillScroller}>
                      {connectedAccounts.map((account) => (
                        <button
                          type="button"
                          key={account.id}
                          className={`${styles.accountPill} ${activeAccountId === account.id
                            ? styles.accountPillActive
                            : ""
                            }`}
                          onClick={() => handleSelectAccount(account.id)}
                        >
                          {account.avatarUrl ? (
                            <img
                              src={account.avatarUrl}
                              alt=""
                              className={styles.accountPillAvatar}
                            />
                          ) : (
                            <span className={styles.accountPillAvatarFallback}>
                              {account.username?.[0]?.toUpperCase() || "?"}
                            </span>
                          )}
                          @{account.username}
                          <span className={styles.accountPillStatusDot} />
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      className={styles.accountSwitcherConnectButton}
                      onClick={handleConnectInstagram}
                    >
                      <FiPlus />
                      Connect IG Account
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div
              className={styles.fixedPageHeaderSpacer}
              style={{ height: fixedBarHeight }}
              aria-hidden="true"
            />

            <div className={styles.connectGateCard}>
              <div className={styles.instagramIcon}>
                <img src={instagramPng} alt="Instagram" />
              </div>

              <h2>Connect your Instagram account first</h2>

              <p>
                Automation rules run against your connected Instagram
                account&apos;s comments and direct messages. Connect your
                account to start building keyword-triggered replies.
              </p>

              <Link
                to="/dashboard/instagram"
                className={styles.connectGateButton}
              >
                <FiLock />
                Go to Instagram Integration
              </Link>
            </div>
          </div>
        </main>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <main className="content">
        <div className={styles.instagramPage}>
          <div
            ref={stickyHeaderRef}
            className={styles.fixedPageHeader}
            style={{
              top: stickyHeaderOffset,
              left: fixedBarLeft,
              width: fixedBarWidth,
            }}
          >
            <div className={styles.fixedPageHeaderInner}>
              <div className={styles.pageHeaderLeft}>
                <Link
                  to="/dashboard/instagram"
                  className={styles.backButtonCompact}
                  aria-label="Back to Instagram"
                >
                  <FiArrowLeft />
                </Link>
                <div className={styles.pageHeaderIcon}>
                  <img src={instagramPng} alt="Instagram" />
                </div>
                <div className={styles.pageHeaderTitleRow}>
                  <h1>Instagram Automation Setup</h1>
                </div>
              </div>
              {isConnected && (
                <div className={styles.pageHeaderRight}>
                  <div className={styles.accountPillScroller}>
                    {connectedAccounts.map((account) => (
                      <button
                        type="button"
                        key={account.id}
                        className={`${styles.accountPill} ${activeAccountId === account.id
                          ? styles.accountPillActive
                          : ""
                          }`}
                        onClick={() => handleSelectAccount(account.id)}
                      >
                        {account.avatarUrl ? (
                          <img
                            src={account.avatarUrl}
                            alt=""
                            className={styles.accountPillAvatar}
                          />
                        ) : (
                          <span className={styles.accountPillAvatarFallback}>
                            {account.username?.[0]?.toUpperCase() || "?"}
                          </span>
                        )}
                        @{account.username}
                        <span className={styles.accountPillStatusDot} />
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    className={styles.accountSwitcherConnectButton}
                    onClick={handleConnectInstagram}
                  >
                    <FiPlus />
                    Connect IG Account
                  </button>
                </div>
              )}
            </div>
          </div>
          <div
            className={styles.fixedPageHeaderSpacer}
            style={{ height: fixedBarHeight }}
            aria-hidden="true"
          />

          {scopedMediaId && (
            <div className={styles.mediaScopeBanner}>
              {(() => {
                const info = getPostInfo(scopedMediaId);
                return info?.thumbnail ? (
                  <img
                    src={info.thumbnail}
                    alt="Selected post"
                    className={styles.mediaScopeThumb}
                  />
                ) : (
                  <div className={styles.mediaScopeThumbFallback}>
                    <FiImage />
                  </div>
                );
              })()}

              <div className={styles.mediaScopeInfo}>
                <strong>Automations apply only to this post</strong>
                <p>
                  {getPostInfo(scopedMediaId)?.caption ||
                    `Post ID: ${scopedMediaId}`}
                </p>
              </div>

              <div className={styles.mediaScopeActionsRow}>
                <div className={styles.postSelectorContainer}>
                  <button
                    type="button"
                    className={styles.postSelectorButton}
                    onClick={() => setPostSelectorOpen(!postSelectorOpen)}
                  >
                    <span className={styles.postSelectorLabel}>
                      Post: {scopedMediaId}
                    </span>
                    <FiChevronDown
                      className={`${styles.postSelectorIcon} ${postSelectorOpen ? styles.postSelectorIconOpen : ""
                        }`}
                    />
                  </button>

                  {postSelectorOpen && Object.keys(mediaMap).length > 0 && (
                    <div className={styles.postSelectorDropdown}>
                      {Object.values(mediaMap).map((post) => (
                        <button
                          key={post.mediaId}
                          type="button"
                          className={`${styles.postSelectorItem} ${post.mediaId === scopedMediaId
                            ? styles.postSelectorItemActive
                            : ""
                            }`}
                          onClick={() => handleSelectPost(post.mediaId)}
                        >
                          <div className={styles.postSelectorItemThumb}>
                            {post.thumbnail ? (
                              <img
                                src={post.thumbnail}
                                alt={`Post ${post.mediaId}`}
                              />
                            ) : (
                              <FiImage className={styles.postSelectorItemIcon} />
                            )}
                          </div>
                          <div className={styles.postSelectorItemContent}>
                            <div className={styles.postSelectorItemId}>
                              Post: {post.mediaId}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  className={styles.mediaScopeClearButton}
                  onClick={handleClearMediaScope}
                >
                  View All Rules
                </button>
              </div>
            </div>
          )}

          {statusMessage && (
            <div
              className={
                statusType === "error"
                  ? styles.errorMessage
                  : styles.successMessage
              }
            >
              {statusMessage}
            </div>
          )}

          <div className={styles.automationLayout}>
            {/* ---- Rule builder form ---- */}
            <section className={styles.dataCard}>
              {editingRuleId && !scopedMediaId && (
                <div
                  className={styles.mediaScopeBanner}
                  style={{ marginBottom: 18 }}
                >
                  {(() => {
                    const info = getPostInfo(editingRuleMediaId);
                    return (
                      <>
                        {info?.thumbnail ? (
                          <img
                            src={info.thumbnail}
                            alt="Post this rule belongs to"
                            className={styles.mediaScopeThumb}
                          />
                        ) : (
                          <div className={styles.mediaScopeThumbFallback}>
                            <FiImage />
                          </div>
                        )}

                        <div className={styles.mediaScopeInfo}>
                          <strong>Editing a rule for this post</strong>
                          <p>
                            {info?.caption
                              ? info.caption
                              : editingRuleMediaId
                                ? `Post ID: ${editingRuleMediaId}`
                                : "Post details unavailable"}
                          </p>
                        </div>
                      </>
                    );
                  })()}

                  <button
                    type="button"
                    className={styles.mediaScopeClearButton}
                    onClick={resetForm}
                  >
                    Cancel edit
                  </button>
                </div>
              )}

              <div className={styles.dataHeader}>
                <div>
                  <h2>
                    {editingRuleId
                      ? "Edit automation"
                      : scopedMediaId
                        ? "Create automation"
                        : "Select a post"}
                  </h2>
                  <p>
                    {editingRuleId
                      ? "Update the trigger or reply sequence."
                      : scopedMediaId
                        ? "Define the trigger, destination, and reply sequence."
                        : "Every rule is tied to one Instagram post."}
                  </p>
                </div>
              </div>

              {editingRuleId || scopedMediaId ? (
                <div
                  className={styles.automationForm}
                  style={{ maxWidth: "none", marginTop: 16 }}
                >
                  {/* <div className={styles.formGroup}>
                    <label>Trigger</label>
                    <div className={styles.segmentedControl} role="tablist">
                      {TRIGGER_TYPES.map(({ value, label, icon: Icon }) => (
                        <button
                          key={value}
                          type="button"
                          role="tab"
                          aria-selected={triggerType === value}
                          className={`${styles.segmentedOption} ${triggerType === value
                            ? styles.segmentedOptionActive
                            : ""
                            }`}
                          onClick={() => setTriggerType(value)}
                        >
                          <Icon />
                          {label}
                        </button>
                      ))}
                    </div>
                    <p className={styles.fieldHint}>
                      {triggerType === "sentiment"
                        ? "Fires when a comment is classified with a selected mood."
                        : "Fires when a comment contains any of the keywords below."}
                    </p>
                  </div> */}

                  {triggerType === "keyword" ? (
                    <div className={styles.formGroup}>
                      <label>Keywords</label>
                      <div className={styles.keywordInputBox}>
                        {keywords.map((keyword) => (
                          <span key={keyword} className={styles.keywordChip}>
                            {keyword}
                            <button
                              type="button"
                              className={styles.keywordChipRemove}
                              onClick={() => removeKeyword(keyword)}
                              aria-label={`Remove keyword ${keyword}`}
                            >
                              <FiX />
                            </button>
                          </span>
                        ))}
                        <input
                          type="text"
                          className={styles.keywordRawInput}
                          placeholder={
                            keywords.length >= MAX_KEYWORDS
                              ? "Maximum keywords reached"
                              : keywords.length
                                ? "Add another..."
                                : "Example: pricing, discount"
                          }
                          value={keywordDraft}
                          maxLength={MAX_KEYWORD_LENGTH}
                          disabled={keywords.length >= MAX_KEYWORDS}
                          onChange={(e) => setKeywordDraft(e.target.value)}
                          onKeyDown={handleKeywordKeyDown}
                          onBlur={commitKeywordDraft}
                        />
                      </div>
                      <p className={styles.fieldHint}>
                        Press Enter to add · {keywords.length}/{MAX_KEYWORDS}
                      </p>
                    </div>
                  ) : (
                    <div className={styles.formGroup}>
                      <label>Sentiments</label>
                      <div className={styles.sentimentOptionGroup}>
                        {SENTIMENT_OPTIONS.map(
                          ({ value, label, icon: Icon, chipClass }) => {
                            const selected = sentiments.includes(value);
                            return (
                              <button
                                key={value}
                                type="button"
                                className={`${styles.sentimentOption} ${styles[chipClass]
                                  } ${selected ? styles.sentimentOptionSelected : ""
                                  }`}
                                onClick={() => toggleSentiment(value)}
                                aria-pressed={selected}
                              >
                                <Icon />
                                {label}
                              </button>
                            );
                          },
                        )}
                      </div>
                      <p className={styles.fieldHint}>
                        Select one or more moods to match.
                      </p>
                    </div>
                  )}

                  <div className={styles.formGroup}>
                    <label>Reply destination</label>
                    <div className={styles.destinationGrid}>
                      {MATCH_LOCATIONS.map(
                        ({ value, label, description, icon: Icon }) => (
                          <button
                            key={value}
                            type="button"
                            className={`${styles.destinationCard} ${matchLocation === value
                              ? styles.destinationCardActive
                              : ""
                              }`}
                            onClick={() => handleMatchLocationChange(value)}
                          >
                            <span className={styles.destinationCardIcon}>
                              <Icon />
                            </span>
                            <span className={styles.destinationCardText}>
                              <strong>
                                {label}
                                {value === "conversation" && (
                                  <span
                                    className={styles.infoIconBtn}
                                    role="button"
                                    tabIndex={0}
                                    aria-label="About the 24-hour messaging window"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowDmWindowInfo(true);
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.stopPropagation();
                                        setShowDmWindowInfo(true);
                                      }
                                    }}
                                  >
                                    <FiInfo size={13} />
                                  </span>
                                )}
                              </strong>
                              <span>{description}</span>
                            </span>
                            {matchLocation === value && (
                              <span className={styles.destinationCardCheck} aria-hidden>
                                ✓
                              </span>
                            )}
                          </button>
                        ),
                      )}
                    </div>
                    <p className={styles.fieldHint}>
                      {matchLocation === "comment"
                        ? "Publicly reply under matching comments. Use Direct Message for audio or video."
                        : "Reply in the commenter's Instagram inbox."}
                    </p>
                  </div>

                  <div className={styles.formGroup}>
                    <label>Reply Sequence</label>
                    <p className={styles.fieldHint}>
                      Sent in order when the trigger matches. Each type once per
                      rule.
                    </p>

                    <div className={styles.blockList}>
                      {replyBlocks.map((block, index) => {
                        const meta = blockMeta(block.type);
                        const Icon = meta.icon;
                        const needsCaption = block.type !== "text";

                        return (
                          <div key={block.id} className={styles.blockCard}>
                            <div className={styles.blockOrder}>
                              <span className={styles.blockOrderNumber}>
                                {index + 1}
                              </span>
                              <div className={styles.blockOrderButtons}>
                                <button
                                  type="button"
                                  className={styles.blockOrderButton}
                                  disabled={index === 0}
                                  onClick={() => moveBlock(block.id, -1)}
                                  aria-label="Move block up"
                                >
                                  <FiArrowUp />
                                </button>
                                <button
                                  type="button"
                                  className={styles.blockOrderButton}
                                  disabled={index === replyBlocks.length - 1}
                                  onClick={() => moveBlock(block.id, 1)}
                                  aria-label="Move block down"
                                >
                                  <FiArrowDown />
                                </button>
                              </div>
                            </div>

                            <div className={styles.blockBody}>
                              <div className={styles.blockTypeRow}>
                                <select
                                  className={styles.blockTypeSelect}
                                  value={block.type}
                                  onChange={(e) =>
                                    changeBlockType(block.id, e.target.value)
                                  }
                                >
                                  {availableBlockTypes(matchLocation)
                                    .filter(
                                      (option) =>
                                        option.value === block.type ||
                                        !usedBlockTypes.includes(option.value),
                                    )
                                    .map((option) => (
                                      <option
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </option>
                                    ))}
                                </select>

                                <button
                                  type="button"
                                  className={styles.blockRemoveButton}
                                  onClick={() => removeBlock(block.id)}
                                  disabled={replyBlocks.length === 1}
                                  aria-label="Remove block"
                                >
                                  <FiTrash2 />
                                </button>
                              </div>

                              {block.type === "text" ? (
                                <textarea
                                  className={styles.blockContentInput}
                                  rows={3}
                                  placeholder={meta.placeholder}
                                  value={block.content}
                                  onChange={(e) =>
                                    updateBlock(block.id, {
                                      content: e.target.value,
                                    })
                                  }
                                />
                              ) : (
                                <input
                                  type="text"
                                  className={styles.blockContentInput}
                                  placeholder={meta.placeholder}
                                  value={block.content}
                                  onChange={(e) =>
                                    updateBlock(block.id, {
                                      content: e.target.value,
                                    })
                                  }
                                />
                              )}

                            {/* caption for img/url/video/audio */}
                              {/* {needsCaption && (
                                <input
                                  type="text"
                                  className={styles.blockSecondaryInput}
                                  placeholder={
                                    block.type === "url"
                                      ? "Link text shown to the user (optional)"
                                      : "Caption (optional)"
                                  }
                                  value={block.caption}
                                  onChange={(e) =>
                                    updateBlock(block.id, {
                                      caption: e.target.value,
                                    })
                                  }
                                />
                              )} */}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className={styles.addBlockRow}>
                      {availableBlockTypes(matchLocation).map(
                        ({ value, label, icon: Icon }) => {
                          const alreadyUsed = usedBlockTypes.includes(value);
                          return (
                            <button
                              key={value}
                              type="button"
                              className={styles.automationAddBlockButton}
                              onClick={() => addBlock(value)}
                              disabled={alreadyUsed}
                              title={
                                alreadyUsed
                                  ? `${label} reply already added`
                                  : undefined
                              }
                            >
                              <FiPlus />
                              <Icon />
                              {label}
                            </button>
                          );
                        },
                      )}
                    </div>
                  </div>

                  <div className={styles.formActionsRow}>
                    <button
                      type="button"
                      className={styles.automationSaveButton}
                      disabled={saving}
                      onClick={handleSaveRule}
                    >
                      {saving
                        ? "Saving..."
                        : editingRuleId
                          ? "Update automation"
                          : "Create automation"}
                    </button>

                    {editingRuleId && (
                      <button
                        type="button"
                        className={styles.cancelEditButton}
                        onClick={resetForm}
                        disabled={saving}
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div
                  className={styles.ruleEmptyState}
                  style={{ padding: "40px 20px" }}
                >
                  <p style={{ marginBottom: 16 }}>
                    Automation rules are created for a specific post — there are
                    no account-wide rules. Open your Instagram Posts and click
                    &quot;Automate Reply&quot; on a post to create one.
                  </p>
                  <Link
                    to="/dashboard/instagram"
                    className={styles.connectGateButton}
                  >
                    <FiZap />
                    Go to Instagram Posts
                  </Link>
                </div>
              )}
            </section>

            {/* ---- Existing rules (right column) ---- */}
            <section className={styles.rulesPanel}>
              <div className={styles.rulesPanelHeader}>
                <div>
                  <h2>Rules</h2>
                  <p className={styles.rulesPanelMeta}>
                    {rules.filter((r) => r.isActive).length} active ·{" "}
                    {rules.length} total
                  </p>
                </div>
                <button
                  type="button"
                  className={styles.ruleTesterOpenButton}
                  onClick={() => setTesterModalOpen(true)}
                >
                  <FiMessageSquare />
                  Test Rules
                </button>
              </div>

              {rulesLoading ? (
                <p className={styles.ruleEmptyState}>Loading rules...</p>
              ) : rules.length === 0 ? (
                <div className={styles.ruleEmptyState}>
                  <p>
                    {scopedMediaId
                      ? "No automation rules for this post yet. Create one on the left to get started."
                      : "No automation rules yet. Create one on the left to get started."}
                  </p>
                </div>
              ) : (
                <div className={styles.ruleList}>
                  {paginatedRules.map((rule) => {
                    const LocationIcon =
                      MATCH_LOCATIONS.find(
                        (m) => m.value === rule.matchLocation,
                      )?.icon || FiZap;

                    const postInfo = getPostInfo(rule.mediaId);

                    return (
                      <div
                        key={rule.id}
                        className={`${styles.ruleCard} ${!rule.isActive ? styles.ruleCardInactive : ""
                          }`}
                      >
                        {!scopedMediaId && (
                          <div className={styles.ruleMediaBadge}>
                            {postInfo?.thumbnail ? (
                              <img
                                src={postInfo.thumbnail}
                                alt="Post this rule belongs to"
                                className={styles.ruleMediaBadgeThumb}
                              />
                            ) : (
                              <FiImage />
                            )}
                            {postInfo?.caption
                              ? postInfo.caption
                              : rule.mediaId
                                ? `Post ID: ${rule.mediaId}`
                                : "Post unavailable"}
                          </div>
                        )}

                        <div className={styles.ruleCardTop}>
                          <div className={styles.ruleCardTitleBlock}>
                            <div
                              className={styles.ruleMetaRow}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: 8,
                              }}
                            >
                              <span
                                className={styles.ruleMetaText}
                                style={{ flexShrink: 0 }}
                              >
                                {rule.triggerType === "sentiment"
                                  ? "Sentiment"
                                  : "Keyword"}
                                :
                              </span>
                              <div
                                style={{
                                  flex: 1,
                                  minWidth: 0,
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 8,
                                }}
                              >
                                {rule.triggerType === "sentiment"
                                  ? (rule.sentiments || []).map((sentiment) => {
                                    const option = SENTIMENT_OPTIONS.find(
                                      (s) => s.value === sentiment,
                                    );
                                    const Icon = option?.icon || FiActivity;
                                    return (
                                      <span
                                        key={sentiment}
                                        className={`${styles.ruleSentimentChip} ${styles[
                                          option?.chipClass ||
                                          "sentimentChipNeutral"
                                        ]
                                          }`}
                                      >
                                        <Icon />
                                        {option?.label || sentiment}
                                      </span>
                                    );
                                  })
                                  : (rule.keywords || []).map((keyword) => (
                                    <span
                                      key={keyword}
                                      className={styles.keywordChip}
                                    >
                                      {keyword}
                                    </span>
                                  ))}
                              </div>
                            </div>
                            <div className={styles.ruleMetaRow}>
                              <span className={styles.ruleMetaText}>
                                {rule.matchLocation === "conversation"
                                  ? "DM reply"
                                  : "Comment reply"}
                              </span>
                            </div>
                          </div>

                          <button
                            type="button"
                            className={`${styles.ruleStatusToggle} ${rule.isActive
                              ? styles.ruleStatusToggleOn
                              : styles.ruleStatusToggleOff
                              }`}
                            disabled={togglingId === rule.id}
                            onClick={() => handleToggleRule(rule)}
                            aria-label={
                              rule.isActive ? "Turn rule off" : "Turn rule on"
                            }
                          >
                            <FiPower />
                            {togglingId === rule.id
                              ? "..."
                              : rule.isActive
                                ? "Active"
                                : "Inactive"}
                          </button>
                        </div>

                        <div className={styles.ruleSequencePreview}>
                          {(rule.replyBlocks || []).map((block, index) => {
                            const meta = blockMeta(block.type);
                            const text = (block.content || "").trim();
                            const blockKey = `${rule.id}-${block.id || index}`;
                            const isClamped = !!clampedBlocks[blockKey];
                            return (
                              <span
                                key={block.id || index}
                                className={styles.ruleBlockChip}
                              >
                                <span className={styles.ruleBlockType}>
                                  {meta.label}
                                </span>
                                <span
                                  className={styles.ruleBlockChipText}
                                  ref={(el) => measureReplyBlock(el, blockKey)}
                                >
                                  {text || "Empty"}
                                </span>
                                {isClamped && (
                                  <button
                                    type="button"
                                    className={styles.ruleReadMoreButton}
                                    onClick={() => setReadMoreRule(rule)}
                                  >
                                    Read more
                                  </button>
                                )}
                              </span>
                            );
                          })}
                        </div>

                        <div className={styles.ruleActionsRow}>
                          <button
                            type="button"
                            className={`${styles.ruleActionButton} ${styles.automationEditButton}`}
                            onClick={() => handleEditRule(rule)}
                          >
                            <FiEdit2 />
                            Edit
                          </button>

                          <button
                            type="button"
                            className={`${styles.ruleActionButton} ${styles.ruleDeleteButton}`}
                            onClick={() => setConfirmDeleteId(rule.id)}
                          >
                            <FiTrash2 />
                            Delete
                          </button>
                        </div>

                        {confirmDeleteId === rule.id && (
                          <div className={styles.ruleDeleteConfirmRow}>
                            <FiAlertTriangle />
                            <span style={{ flex: 1 }}>
                              Delete this rule permanently?
                            </span>
                            <button
                              type="button"
                              className={styles.ruleActionButton}
                              onClick={() => setConfirmDeleteId(null)}
                            >
                              Keep
                            </button>
                            <button
                              type="button"
                              className={`${styles.ruleActionButton} ${styles.ruleDeleteButton}`}
                              disabled={deletingId === rule.id}
                              onClick={() => handleDeleteRule(rule.id)}
                            >
                              {deletingId === rule.id
                                ? "Deleting..."
                                : "Delete"}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {!rulesLoading && rules.length > RULES_PAGE_SIZE && (
                <div className={styles.tablePagination}>
                  <span className={styles.tablePaginationInfo}>
                    Page <strong>{rulesPage}</strong> of{" "}
                    <strong>{totalRulesPages}</strong> ·{" "}
                    <strong>{rules.length}</strong> rules
                  </span>
                  <div className={styles.tablePaginationControls}>
                    <button
                      type="button"
                      className={styles.paginationButton}
                      disabled={rulesPage === 1}
                      onClick={() => setRulesPage((p) => Math.max(1, p - 1))}
                      aria-label="Previous page"
                    >
                      <FiChevronLeft />
                    </button>
                    {getRulesPageNumbers(rulesPage, totalRulesPages).map(
                      (pageNum, idx) =>
                        pageNum === "..." ? (
                          <span
                            key={`ellipsis-${idx}`}
                            className={styles.paginationEllipsis}
                          >
                            …
                          </span>
                        ) : (
                          <button
                            key={pageNum}
                            type="button"
                            className={`${styles.paginationButton} ${
                              pageNum === rulesPage
                                ? styles.paginationButtonActive
                                : ""
                            }`}
                            onClick={() => setRulesPage(pageNum)}
                            aria-current={
                              pageNum === rulesPage ? "page" : undefined
                            }
                          >
                            {pageNum}
                          </button>
                        ),
                    )}
                    <button
                      type="button"
                      className={styles.paginationButton}
                      disabled={rulesPage === totalRulesPages}
                      onClick={() =>
                        setRulesPage((p) => Math.min(totalRulesPages, p + 1))
                      }
                      aria-label="Next page"
                    >
                      <FiChevronRight />
                    </button>
                  </div>
                </div>
              )}
            </section>

          </div>

          {testerModalOpen && (
            <div
              className={styles.ruleTesterOverlay}
              role="dialog"
              aria-modal="true"
              aria-labelledby="rule-tester-title"
            >
              <button
                type="button"
                className={styles.ruleTesterBackdrop}
                aria-label="Close rule tester"
                onClick={() => setTesterModalOpen(false)}
              />
              <div className={styles.ruleTesterModal}>
                <div className={styles.ruleTesterModalHeader}>
                  <div className={styles.ruleTesterHeaderLeft}>
                    <span className={styles.ruleTesterIcon}>
                      <FiMessageSquare />
                    </span>
                    <h2 id="rule-tester-title">Interactive Rule Tester</h2>
                  </div>
                  <button
                    type="button"
                    className={styles.ruleTesterCloseButton}
                    onClick={() => setTesterModalOpen(false)}
                    aria-label="Close"
                  >
                    <FiX />
                  </button>
                </div>

                <p className={styles.ruleTesterSubtitle}>
                  {rules.length > 0 ? (
                    <>
                      Type keywords or sentiment labels like
                      {(() => {
                        const samples = rules
                          .slice(0, 4)
                          .flatMap((rule) =>
                            rule.triggerType === "sentiment"
                              ? (rule.sentiments || []).slice(0, 1)
                              : (rule.keywords || []).slice(0, 1),
                          )
                          .filter(Boolean)
                          .slice(0, 3);
                        if (samples.length === 0) {
                          return "those on your active rules ";
                        }
                        return samples.map((sample, index, arr) => (
                          <React.Fragment key={sample + index}>
                            <strong>&quot;{String(sample).toUpperCase()}&quot;</strong>
                            {index < arr.length - 1 ? ", " : " "}
                          </React.Fragment>
                        ));
                      })()}
                      to test rule auto-responses in real-time.
                    </>
                  ) : (
                    "Create a rule first, then test it here without waiting for a real comment."
                  )}
                </p>

                <div
                  className={styles.ruleTesterChatWindow}
                  ref={chatWindowRef}
                >
                  {chatMessages.length === 0 ? (
                    <div className={styles.ruleTesterChatEmpty}>
                      <FiMessageSquare />
                      <p>
                        Send a sample comment below to see how a rule reacts.
                      </p>
                    </div>
                  ) : (
                    chatMessages.map((msg) => {
                      if (msg.role === "user") {
                        return (
                          <div
                            key={msg.id}
                            className={`${styles.ruleTesterBubbleRow} ${styles.ruleTesterBubbleRowUser}`}
                          >
                            <span
                              className={`${styles.ruleTesterBubble} ${styles.ruleTesterBubbleUser}`}
                            >
                              {msg.text}
                            </span>
                          </div>
                        );
                      }

                      if (msg.kind === "matched") {
                        const isDm = msg.location === "conversation";
                        return (
                          <div
                            key={msg.id}
                            className={`${styles.ruleTesterBubbleRow} ${styles.ruleTesterBubbleRowSystem}`}
                          >
                            <div
                              className={`${styles.ruleTesterResultCard} ${isDm
                                ? styles.ruleTesterResultCardDm
                                : styles.ruleTesterResultCardComment
                                }`}
                            >
                              <p className={styles.ruleTesterResultCardHeading}>
                                {isDm ? (
                                  <>
                                    <FiSend />
                                    Instagram DM sent
                                  </>
                                ) : (
                                  <>
                                    <FiMessageSquare />
                                    Comment reply posted
                                  </>
                                )}
                              </p>
                              <p className={styles.ruleTesterResultCardMeta}>
                                Matched{" "}
                                {msg.triggerType === "sentiment"
                                  ? "sentiment"
                                  : "keyword"}
                                : <strong>{msg.matchedKeyword}</strong>
                              </p>
                              <div className={styles.ruleSequencePreview}>
                                {msg.blocks.map((block, index) => {
                                  const meta = blockMeta(block.type);
                                  const Icon = meta.icon;
                                  return (
                                    <span
                                      key={block.id || index}
                                      className={styles.ruleBlockChip}
                                    >
                                      <Icon />
                                      {block.content || meta.label}
                                    </span>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div
                          key={msg.id}
                          className={`${styles.ruleTesterBubbleRow} ${styles.ruleTesterBubbleRowSystem}`}
                        >
                          <span
                            className={`${styles.ruleTesterBubble} ${styles.ruleTesterBubbleSystem} ${msg.kind === "no-match"
                              ? styles.ruleTesterBubbleNoMatch
                              : ""
                              }`}
                          >
                            {msg.text}
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>

                <div className={styles.ruleTesterInputRow}>
                  <input
                    type="text"
                    className={styles.ruleTesterChatInput}
                    placeholder="Type 'PRICING' or 'POSITIVE'..."
                    value={chatInput}
                    disabled={rules.length === 0}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleSendTestMessage();
                      }
                    }}
                  />
                  <button
                    type="button"
                    className={styles.ruleTesterSendButton}
                    disabled={rules.length === 0 || !chatInput.trim()}
                    onClick={handleSendTestMessage}
                    aria-label="Send test message"
                  >
                    <FiSend />
                  </button>
                </div>

                {chatMessages.length > 0 && (
                  <button
                    type="button"
                    className={styles.ruleTesterClearButton}
                    onClick={handleClearTesterChat}
                  >
                    Clear conversation
                  </button>
                )}
              </div>
            </div>
          )}

          {readMoreRule && (
            <div
              className={styles.replyContentOverlay}
              role="dialog"
              aria-modal="true"
              aria-labelledby="reply-content-title"
            >
              <button
                type="button"
                className={styles.replyContentBackdrop}
                aria-label="Close full reply content"
                onClick={() => setReadMoreRule(null)}
              />
              <div className={styles.replyContentModal}>
                <div className={styles.replyContentModalHeader}>
                  <div className={styles.ruleTesterHeaderLeft}>
                    <span className={styles.ruleTesterIcon}>
                      <FiMessageSquare />
                    </span>
                    <h2 id="reply-content-title">Reply</h2>
                  </div>
                  <button
                    type="button"
                    className={styles.ruleTesterCloseButton}
                    onClick={() => setReadMoreRule(null)}
                    aria-label="Close"
                  >
                    <FiX />
                  </button>
                </div>

                <div className={styles.replyContentBody}>
                  {(readMoreRule.replyBlocks || []).map((block, index) => {
                    const meta = blockMeta(block.type);
                    const text = (block.content || "").trim();
                    return (
                      <div
                        key={block.id || index}
                        className={styles.replyContentBlock}
                      >
                        <span className={styles.ruleBlockType}>
                          {meta.label}
                        </span>
                        <span className={styles.replyContentBlockText}>
                          {text || "Empty"}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {showDmWindowInfo && (
            <div
              className={styles.replyContentOverlay}
              role="dialog"
              aria-modal="true"
              aria-labelledby="dm-window-info-title"
            >
              <button
                type="button"
                className={styles.replyContentBackdrop}
                aria-label="Close"
                onClick={() => setShowDmWindowInfo(false)}
              />
              <div className={styles.replyContentModal}>
                <div className={styles.replyContentModalHeader}>
                  <div className={styles.ruleTesterHeaderLeft}>
                    <span className={styles.ruleTesterIcon}>
                      <FiInfo />
                    </span>
                    <h3 id="dm-window-info-title">
                      24-Hour Messaging Window
                    </h3>
                  </div>
                  <button
                    type="button"
                    className={styles.ruleTesterCloseButton}
                    onClick={() => setShowDmWindowInfo(false)}
                    aria-label="Close"
                  >
                    <FiX />
                  </button>
                </div>

                <div className={styles.replyContentBody}>
                  <p style={{ marginBottom: 10, lineHeight: 1.5, size: 14 }}>
                    Instagram only allows automated DMs to be sent within a{" "}
                    <strong>24-hour window</strong> after the user last
                    messaged on your DM.
                  </p>
                  <p style={{ marginBottom: 10, lineHeight: 1.5, size: 14 }}>
                    Once that window closes, this rule can no longer deliver
                    a DM reply until the user reaches out to you again.
                  </p>
                  <p style={{ lineHeight: 1.5, size: 14 }}>
                    To keep the automation active, make sure the user has messaged you on DM within
                    24 hours — replying resets the window.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </DashboardLayout>
  );
};

export default InstagramAutomationSetup;